import { useLoaderData } from 'react-router';
import { ArrowUpRight } from 'lucide-react';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    CONTENT_CAP_PADDED,
    TrackedWebsiteCta,
    detailCta,
    useIslandSection,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { useProjectBookMeeting } from '../hooks/use-project-book-meeting';
import { useBookMeetingAccordion } from '../hooks/use-book-meeting-accordion';
import {
    BOOK_A_MEETING,
    useProjectEnquiryModal,
} from '../hooks/use-project-enquiry-modal';

// The prompt leads with its own Book-a-Meeting CTA, so blank the island while
// the block crosses the reading line rather than floating a duplicate over it.
const SUPPRESS_ISLAND: IslandMode = { kind: 'hidden' };

export interface ProjectEnquiryPromptProps {
    /** Heading; `{supplier}` is substituted. */
    heading: string;
    /** Supporting line beneath the heading. `{supplier}` is substituted. */
    body: string;
}

/**
 * The project's end-of-gallery enquiry prompt, where the images have just made
 * the case (the legacy page's best-performing QDD moment): a heading and
 * supporting line naming the professional, a primary Book-a-Meeting CTA and a
 * Visit Website referral — the two QDD actions. Booking opens the inline
 * calendar row when the professional offers direct booking, otherwise the
 * project enquiry modal.
 *
 * Mobile renders the soft card from the mobile round; desktop renders the
 * editorial statement in the page's display voice (the product hook's
 * treatment, on the project's dark ground). Self-hides when the professional
 * is unavailable.
 */
export const ProjectEnquiryPrompt = ({
    heading,
    body,
}: ProjectEnquiryPromptProps) => {
    const { project } = useLoaderData<typeof loader>();
    const { mobile } = useAppDisplayModeContext();
    const professional = project.Professional;
    const { canBookDirect } = useProjectBookMeeting();
    const { focusBookMeeting } = useBookMeetingAccordion();
    const { openEnquiryModal } = useProjectEnquiryModal();
    // Book a Meeting is a contact channel — it respects the professional's
    // enquiry audience like the enquiry CTAs elsewhere on the detail pages.
    const { showEnquiryCTA } = useEnquiryVisibility(professional);
    const suppressRef = useIslandSection<HTMLElement>(SUPPRESS_ISLAND);

    if (!professional?.Title) return null;

    const supplier = professional.Title;
    // Same preference as the legacy end-of-gallery banner: the project's own
    // (untracked) website wins over the professional's, so a project that only
    // exposes its own site still gets the referral CTA.
    const websiteUrl = project.WebsiteUntracked || professional.Website;
    // Nothing to offer → render nothing.
    if (!showEnquiryCTA && !websiteUrl) return null;
    const headingText = heading.replace('{supplier}', supplier);
    const bodyText = body.replace('{supplier}', supplier);
    const onBookMeeting = canBookDirect
        ? focusBookMeeting
        : () =>
              openEnquiryModal({
                  title: `${BOOK_A_MEETING} with ${supplier}`,
                  ctaLabel: BOOK_A_MEETING,
              });

    // Desktop — an editorial statement in the page's display voice: the
    // question carries the white, the invitation continues at half opacity,
    // and the actions sit quietly beneath.
    if (!mobile) {
        return (
            <section ref={suppressRef} className="w-full px-[3rem] text-white">
                <div className={CONTENT_CAP_PADDED}>
                    <p className="m-0 max-w-[64rem] font-twk-ghost text-[3rem] leading-[1.02] font-normal tracking-[-0.02em] text-balance">
                        {headingText}{' '}
                        <span className="text-white/50">{bodyText}</span>
                    </p>
                    <div className="mt-[2.5rem] flex items-center gap-3">
                        {showEnquiryCTA ? (
                            <button
                                type="button"
                                onClick={onBookMeeting}
                                className={detailCta('filled-dark')}
                            >
                                {BOOK_A_MEETING}
                            </button>
                        ) : null}
                        {websiteUrl ? (
                            <TrackedWebsiteCta
                                website={websiteUrl}
                                libraryItemID={project.ID}
                                professionalID={professional.ID}
                                professionalTitle={supplier}
                                variant="outlined-dark"
                                source="ProjectWebsiteCTA"
                            >
                                Visit Website
                                <ArrowUpRight className="size-4" />
                            </TrackedWebsiteCta>
                        ) : null}
                    </div>
                </div>
            </section>
        );
    }

    // Mobile — the product PDP's mid-page hook card, in the project's dark theme.
    return (
        <section ref={suppressRef} className="px-4">
            <div className="flex flex-col gap-5 rounded-2xl bg-white/[0.06] p-6 text-white">
                <div className="flex flex-col gap-2">
                    <h3 className="m-0 font-sans text-[1.375rem] leading-[1.15] font-medium tracking-[-0.02em] text-white">
                        {headingText}
                    </h3>
                    <p className="m-0 font-sans text-[0.9375rem] leading-[1.45] text-white/60">
                        {bodyText}
                    </p>
                </div>
                <div className="flex flex-col gap-3">
                    {showEnquiryCTA ? (
                        <button
                            type="button"
                            onClick={onBookMeeting}
                            className={detailCta(
                                'filled-invert',
                                'w-full text-sm',
                            )}
                        >
                            {BOOK_A_MEETING}
                        </button>
                    ) : null}
                    {websiteUrl ? (
                        <TrackedWebsiteCta
                            website={websiteUrl}
                            libraryItemID={project.ID}
                            professionalID={professional.ID}
                            professionalTitle={supplier}
                            variant="outlined-dark"
                            source="ProjectWebsiteCTA"
                            className="w-full text-sm"
                        >
                            Visit Website
                            <ArrowUpRight className="size-4" />
                        </TrackedWebsiteCta>
                    ) : null}
                </div>
            </div>
        </section>
    );
};
